// ─── useAccount.js — resolves account, permissions, and data owner ───────────
import { useState, useEffect, useCallback } from "react";
import { supabase } from "./useData.js";

const OWNER_PERMISSIONS = {
  dashboard:    { view: true },
  orders:       { view: true, create: true, edit: true, delete: true },
  marketplaces: { view: true, create: true, edit: true, delete: true },
  targets:      { view: true, create: true, edit: true, delete: true },
  transactions: { view: true, create: true, edit: true, delete: true },
  planner:      { view: true, create: true, edit: true, delete: true },
  calendar:     { view: true, edit: true },
  modifiers:    { view: true, create: true, edit: true, delete: true },
  settings:     { view: true, edit: true },
  export:       { view: true },
};

export function useAccount(session) {
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [isOwner, setIsOwner] = useState(true);
  const [permissions, setPermissions] = useState(OWNER_PERMISSIONS);
  const [featureAreas, setFeatureAreas] = useState([]);
  const [dataOwnerId, setDataOwnerId] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);

  // ── Check permission helper ────────────────────────────────────────────
  // Usage: can("orders", "edit") → true/false
  const can = useCallback((area, action) => {
    if (isOwner) return true;
    return permissions?.[area]?.[action] === true;
  }, [isOwner, permissions]);

  // ── Resolve account ────────────────────────────────────────────────────
  async function resolve() {
    if (!session) { setAccountLoading(false); return; }
    setAccountLoading(true);
    try {
      const res = await fetch("/api/account", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAccount(data.account);
        setMembers(data.members || []);
        setIsOwner(data.isOwner);
        setPermissions(data.permissions || OWNER_PERMISSIONS);
        setFeatureAreas(data.featureAreas || []);
        setDataOwnerId(data.dataOwnerId);
      } else {
        // Fallback: treat as owner
        setDataOwnerId(session.user.id);
        setIsOwner(true);
        setPermissions(OWNER_PERMISSIONS);
      }
    } catch {
      setDataOwnerId(session.user.id);
      setIsOwner(true);
    } finally {
      setAccountLoading(false);
    }
  }

  useEffect(() => { resolve(); }, [session?.user?.id]);

  // ── Invite a member with permissions ───────────────────────────────────
  async function inviteMember(email, memberPermissions) {
    if (!session || !isOwner) return { error: "Only the account owner can invite" };
    try {
      const res = await fetch("/api/account", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "invite", email, permissions: memberPermissions }),
      });
      // Session expired mid-session → surface a clear, actionable error
      // instead of the raw JWT-parse message.
      if (res.status === 401 || res.status === 403) {
        const body = await res.json().catch(() => ({}));
        const msg = String(body?.error || "");
        if (/jwt|token|expired|unauthorized|invalid claims/i.test(msg) || res.status === 401) {
          return { error: "Your session expired. Please sign out and sign in again to continue.", sessionExpired: true };
        }
        return { error: body?.error || "You don't have permission for this action." };
      }
      const data = await res.json();
      if (res.ok) await resolve(); // refresh members
      return data;
    } catch (e) {
      return { error: "Network error — check your connection and try again" };
    }
  }

  // ── Accept a pending invite (called automatically on login) ────────────
  async function acceptInvite() {
    if (!session) return { error: "Not logged in" };
    try {
      const res = await fetch("/api/account", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: "accept" }),
      });
      const data = await res.json();
      if (res.ok) await resolve();
      return data;
    } catch (e) {
      return { error: "Network error" };
    }
  }

  // ── Update a member's permissions ──────────────────────────────────────
  async function updateMemberPermissions(memberId, newPermissions) {
    if (!session || !isOwner) return { error: "Only the account owner can change permissions" };
    const res = await fetch("/api/account", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "update_permissions", memberId, permissions: newPermissions }),
    });
    const data = await res.json();
    if (res.ok) await resolve(); // refresh
    return data;
  }

  // ── Remove a member ────────────────────────────────────────────────────
  async function removeMember(memberId) {
    if (!session || !isOwner) return { error: "Only the account owner can remove members" };
    const res = await fetch("/api/account", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: "remove", memberId }),
    });
    const data = await res.json();
    if (res.ok) setMembers(ms => ms.filter(m => m.id !== memberId));
    return data;
  }

  return {
    account,
    members,
    isOwner,
    permissions,       // full permissions object
    can,               // helper: can("orders", "edit")
    featureAreas,      // definition of all feature areas for the permission picker
    dataOwnerId,
    accountLoading,
    inviteMember,
    acceptInvite,
    updateMemberPermissions,
    removeMember,
    refreshAccount: resolve,
  };
}
